// ==================== UPGRADE PLAN PAGE ====================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Check, 
  ArrowRight, 
  Crown,
  Users,
  MessageSquare,
  FileText,
  Send,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  AlertTriangle,
  CreditCard,
  Lock,
  Zap,
  RefreshCw,
  Star,
  Shield,
  CheckCircle,
} from 'lucide-react';
import { CouponField } from '../../components/coupons/CouponField';
import type { UpgradeQuote } from '../../services/subscription.service';
import { Button, Badge } from '../../components/ui';
import { usePlans } from '../../hooks';
import { subscriptionService } from '../../services';
import { cn } from '../../utils/helpers';
import { fadeInUp, staggerContainer, hoverScale, tapScale } from '../../utils/animations';
import type { SubscriptionPlan } from '../../types';
import type { Subscription, PaymentMethod } from '../../services/subscription.service';
import { leadGenAddonCycleAmount } from '../../services/subscription.service';
import { ROUTES } from '../../constants/routes.constants';

// ==================== CONSTANTS ====================

const LIMIT_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  contacts: Users,
  messages: MessageSquare,
  templates: FileText,
  campaigns: Send,
};

const formatLimitValue = (value: number): string => {
  if (value === -1) return 'Unlimited';
  return value.toLocaleString();
};

// ==================== TYPES ====================

interface PlanComparison {
  isUpgrade: boolean;
  isDowngrade: boolean;
  isCurrent: boolean;
  limitWarnings: string[];
}

// ==================== PLAN CARD COMPONENT ====================

interface PlanCardProps {
  plan: SubscriptionPlan;
  isYearly: boolean;
  isSelected: boolean;
  comparison: PlanComparison;
  onSelect: () => void;
}

const PlanCard = ({ plan, isYearly, isSelected, comparison, onSelect }: PlanCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const monthlyPrice = typeof plan.monthlyPrice === 'string' ? parseFloat(plan.monthlyPrice) : plan.monthlyPrice;
  const yearlyPrice = plan.yearlyPrice ? (typeof plan.yearlyPrice === 'string' ? parseFloat(plan.yearlyPrice) : plan.yearlyPrice) : monthlyPrice * 10;
  
  const displayPrice = isYearly ? yearlyPrice / 12 : monthlyPrice;
  const isPopular = plan.isPopular;
  const hasMoreFeatures = plan.features.length > 4;
  const displayedFeatures = isExpanded ? plan.features : plan.features.slice(0, 4);

  const limits = [
    { key: 'contacts', label: 'Contacts', value: formatLimitValue(plan.maxContacts) },
    { key: 'messages', label: 'Messages/mo', value: formatLimitValue(plan.maxMessagesPerMonth) },
    { key: 'templates', label: 'Templates', value: formatLimitValue(plan.maxTemplates) },
    { key: 'campaigns', label: 'Campaigns', value: formatLimitValue(plan.maxCampaigns) },
  ];

  const isDisabled = comparison.isCurrent;

  return (
    <motion.div
      variants={fadeInUp}
      whileHover={isDisabled ? undefined : hoverScale}
      whileTap={isDisabled ? undefined : tapScale}
      onClick={isDisabled ? undefined : onSelect}
      className={cn(
        'relative rounded-2xl p-6 transition-all duration-300',
        'border-2',
        isDisabled && 'cursor-not-allowed opacity-75',
        !isDisabled && 'cursor-pointer',
        comparison.isCurrent
          ? 'border-emerald-500 bg-emerald-500/10'
          : isSelected
          ? 'border-(--color-signal-orange) bg-(--color-orange-glow)'
          : 'border-(--color-border-gray) bg-(--color-charcoal-black)/50 hover:border-(--color-signal-orange)/50',
        isPopular && !isSelected && !comparison.isCurrent && 'border-(--color-signal-orange)/30'
      )}
    >
      {/* Current Plan Badge */}
      {comparison.isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge variant="success" className="whitespace-nowrap">
            <CheckCircle size={12} className="mr-1" />
            Current Plan
          </Badge>
        </div>
      )}

      {/* Popular Badge */}
      {isPopular && !comparison.isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge variant="orange" className="whitespace-nowrap">
            <Crown size={12} className="mr-1" />
            Most Popular
          </Badge>
        </div>
      )}

      {/* Selection Indicator */}
      {!comparison.isCurrent && (
        <div className={cn(
          'absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all',
          isSelected
            ? 'border-(--color-signal-orange) bg-(--color-signal-orange)'
            : 'border-(--color-border-gray)'
        )}>
          {isSelected && <Check size={14} className="text-white" />}
        </div>
      )}

      {/* Plan Name */}
      <h3 className="text-xl font-heading font-bold text-white mb-2">{plan.name}</h3>
      
      {/* Price */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span className={cn(
            'text-3xl font-bold',
            comparison.isCurrent ? 'text-emerald-400' : 'text-(--color-signal-orange)'
          )}>
            ${displayPrice.toFixed(2)}
          </span>
          <span className="text-(--color-muted-gray)">/month</span>
        </div>
        {isYearly && (
          <p className="text-xs text-(--color-success) mt-1">
            Billed ${yearlyPrice.toFixed(2)}/year (Save 17%)
          </p>
        )}

        {/* Upgrade/Downgrade Indicator */}
        {!comparison.isCurrent && (
          <div className={cn(
            'mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium',
            comparison.isUpgrade
              ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
              : 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400'
          )}>
            {comparison.isUpgrade ? (
              <>
                <Zap size={12} />
                Upgrade
              </>
            ) : (
              <>
                <AlertTriangle size={12} />
                Downgrade
              </>
            )}
          </div>
        )}
      </div>

      {/* Limits Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {limits.map((limit) => {
          const Icon = LIMIT_ICONS[limit.key] || Users;
          return (
            <div key={limit.key} className="flex items-center gap-2">
              <Icon size={14} className={comparison.isCurrent ? 'text-emerald-400' : 'text-(--color-signal-orange)'} />
              <div>
                <p className="text-xs text-(--color-muted-gray)">{limit.label}</p>
                <p className="text-sm font-medium text-white">{limit.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Limit Warnings for Downgrade */}
      {comparison.limitWarnings.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-yellow-400 mb-1">Limit exceeded:</p>
              <ul className="text-xs text-yellow-300/80 space-y-0.5">
                {comparison.limitWarnings.map((warning, i) => (
                  <li key={i}>• {warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Features */}
      <ul className="space-y-2">
        <AnimatePresence mode="sync">
          {displayedFeatures.map((feature, index) => (
            <motion.li 
              key={`${plan.id}-feature-${index}`}
              initial={index >= 4 ? { opacity: 0, height: 0 } : false}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-start gap-2 text-sm text-(--color-soft-gray)"
            >
              <Check size={14} className="text-(--color-success) shrink-0 mt-0.5" />
              <span>{feature}</span>
            </motion.li>
          ))}
        </AnimatePresence>
        {hasMoreFeatures && (
          <li>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="flex items-center gap-1 text-xs text-(--color-signal-orange) hover:text-(--color-orange-light) 
                         font-medium transition-colors pl-5"
            >
              {isExpanded ? (
                <>
                  <ChevronUp size={14} />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  +{plan.features.length - 4} more features
                </>
              )}
            </button>
          </li>
        )}
      </ul>
    </motion.div>
  );
};

// ==================== PAYMENT METHOD SELECTOR ====================

interface PaymentMethodSelectorProps {
  paymentMethods: PaymentMethod[];
  selectedMethodId: string | null;
  onSelect: (id: string) => void;
  onAddNew: () => void;
}

const cardBrandIcons: Record<string, string> = {
  visa: '💳',
  mastercard: '💳',
  amex: '💳',
  discover: '💳',
  default: '💳',
};

const PaymentMethodSelector = ({ paymentMethods, selectedMethodId, onSelect, onAddNew }: PaymentMethodSelectorProps) => {
  return (
    <div className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <CreditCard size={20} className="text-(--color-signal-orange)" />
          Payment Method
        </h3>
        <div className="flex items-center gap-1 text-xs text-(--color-muted-gray)">
          <Lock size={12} />
          <span>Secured by Stripe</span>
        </div>
      </div>

      {paymentMethods.length === 0 ? (
        <div className="text-center py-6">
          <CreditCard size={32} className="text-(--color-muted-gray) mx-auto mb-3" />
          <p className="text-sm text-(--color-muted-gray) mb-4">No payment methods saved</p>
          <Button variant="secondary" size="sm" onClick={onAddNew}>
            Add Payment Method
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {paymentMethods.map((pm) => (
            <button
              key={pm.id}
              onClick={() => onSelect(pm.id)}
              className={cn(
                'w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all',
                selectedMethodId === pm.id
                  ? 'border-(--color-signal-orange) bg-(--color-orange-glow)'
                  : 'border-(--color-border-gray) hover:border-(--color-signal-orange)/50'
              )}
            >
              <div className="w-12 h-8 bg-gradient-to-br from-gray-700 to-gray-800 rounded flex items-center justify-center">
                <span className="text-lg">{cardBrandIcons[pm.brand?.toLowerCase() || 'default']}</span>
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm text-white font-medium capitalize">
                  {pm.brand || 'Card'} •••• {pm.last4}
                </p>
                <p className="text-xs text-(--color-muted-gray)">
                  Expires {pm.expiryMonth}/{pm.expiryYear}
                </p>
              </div>
              {pm.isDefault && (
                <Badge variant="success" size="sm">
                  <Star size={10} className="mr-1" />
                  Default
                </Badge>
              )}
              <div className={cn(
                'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                selectedMethodId === pm.id
                  ? 'border-(--color-signal-orange) bg-(--color-signal-orange)'
                  : 'border-(--color-border-gray)'
              )}>
                {selectedMethodId === pm.id && <Check size={12} className="text-white" />}
              </div>
            </button>
          ))}
          
          <button
            onClick={onAddNew}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed 
                       border-(--color-border-gray) text-sm text-(--color-muted-gray) 
                       hover:border-(--color-signal-orange)/50 hover:text-(--color-signal-orange) transition-all"
          >
            <CreditCard size={16} />
            Use a different payment method
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== ORDER SUMMARY COMPONENT ====================

interface OrderSummaryProps {
  currentPlan: { name: string; price: number } | null;
  newPlan: SubscriptionPlan | null;
  isYearly: boolean;
  isUpgrade: boolean;
  /** The Lead Gen add-on carries over and is charged for the new period too. */
  includesLeadGenAddon: boolean;
  /** Organizations purchased: the plan price is charged for each */
  organizationCount: number;
  quote?: UpgradeQuote | null;
}

const OrderSummary = ({ currentPlan, newPlan, isYearly, isUpgrade, includesLeadGenAddon, organizationCount, quote }: OrderSummaryProps) => {
  if (!newPlan) return null;

  const monthlyPrice = typeof newPlan.monthlyPrice === 'string' 
    ? parseFloat(newPlan.monthlyPrice) 
    : newPlan.monthlyPrice;
  const yearlyPrice = newPlan.yearlyPrice 
    ? (typeof newPlan.yearlyPrice === 'string' ? parseFloat(newPlan.yearlyPrice) : newPlan.yearlyPrice) 
    : monthlyPrice * 10;
  
  const newPlanAmount = quote?.planAmount ?? (isYearly ? yearlyPrice : monthlyPrice) * organizationCount;
  const perOrganization = organizationCount > 1 ? ` × ${organizationCount} accounts` : '';
  const addonAmount = quote?.leadGenAddon ?? (includesLeadGenAddon
    ? leadGenAddonCycleAmount(Number(newPlan.leadGenAddonFee ?? 0), isYearly)
    : 0);
  const totalAmount = quote?.amountDueNow ?? (newPlanAmount + addonAmount);

  return (
    <div className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-xl p-5">
      <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>
      
      <div className="space-y-3">
        {currentPlan && (
          <div className="flex justify-between text-sm">
            <span className="text-(--color-muted-gray)">Current: {currentPlan.name}</span>
            <span className="text-(--color-muted-gray) line-through">${(currentPlan.price * organizationCount).toFixed(2)}</span>
          </div>
        )}
        
        <div className="flex justify-between text-sm">
          <span className="text-(--color-soft-gray)">
            {newPlan.name} Plan ({isYearly ? 'Yearly' : 'Monthly'}){perOrganization}
          </span>
          <span className="text-white font-medium">${newPlanAmount.toFixed(2)}</span>
        </div>

        {addonAmount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-(--color-soft-gray)">
              Lead Generation Add-on ({isYearly ? '12 months' : 'monthly'})
            </span>
            <span className="text-white font-medium">${addonAmount.toFixed(2)}</span>
          </div>
        )}

        {!!quote?.discountAmount && (
          <div className="flex justify-between text-sm text-(--color-success)">
            <span>Discount ({quote.coupon?.code})</span>
            <span>−${Number(quote.discountAmount).toFixed(2)}</span>
          </div>
        )}
        <div className="border-t border-(--color-border-gray) pt-3 mt-3">
          <div className="flex justify-between">
            <span className="text-white font-semibold">
              {quote?.coupon ? 'Amount Due Today' : isUpgrade ? 'Amount Due Today' : 'New Recurring Amount'}
            </span>
            <span className="text-xl font-bold text-(--color-signal-orange)">${totalAmount.toFixed(2)}</span>
          </div>
          <p className="text-xs text-(--color-muted-gray) mt-1">
            {quote?.coupon ? `Then $${Number(quote.nextRenewalAmount).toFixed(2)}/${isYearly ? 'year' : 'month'}` : isUpgrade
              ? 'You will be charged the difference prorated for your billing cycle'
              : `Billed ${isYearly ? 'annually' : 'monthly'} starting next billing cycle`
            }
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const UpgradePlanPage = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [upgradeQuote, setUpgradeQuote] = useState<{ key: string; data: UpgradeQuote } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  
  const { plans, isLoading: plansLoading, error: plansError } = usePlans();

  const selectedPlan = plans.find(p => p.id === selectedPlanId) || null;
  const currentPlan = plans.find(p => p.id === currentSubscription?.plan?.id) || null;

  // Fetch current subscription
  const fetchSubscription = useCallback(async () => {
    try {
      setLoadingSubscription(true);
      const response = await subscriptionService.getCurrentSubscription();
      if (response.success && response.data) {
        setCurrentSubscription(response.data);
        // Set billing cycle from current subscription
        setIsYearly(response.data.billingCycle === 'YEARLY');
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoadingSubscription(false);
    }
  }, []);

  // Fetch payment methods
  const fetchPaymentMethods = useCallback(async () => {
    try {
      setLoadingPaymentMethods(true);
      const response = await subscriptionService.getPaymentMethods();
      if (response.success && response.data) {
        setPaymentMethods(response.data);
        // Auto-select default payment method
        const defaultMethod = response.data.find(pm => pm.isDefault);
        if (defaultMethod) {
          setSelectedPaymentMethodId(defaultMethod.id);
        } else if (response.data.length > 0) {
          setSelectedPaymentMethodId(response.data[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch payment methods:', err);
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
    fetchPaymentMethods();
  }, [fetchSubscription, fetchPaymentMethods]);

  // Compare plan to current subscription
  const comparePlan = useCallback((plan: SubscriptionPlan): PlanComparison => {
    const isCurrent = currentSubscription?.plan?.id === plan.id;
    
    if (isCurrent) {
      return { isCurrent: true, isUpgrade: false, isDowngrade: false, limitWarnings: [] };
    }

    if (!currentPlan) {
      return { isCurrent: false, isUpgrade: true, isDowngrade: false, limitWarnings: [] };
    }

    // Compare prices to determine upgrade/downgrade
    const currentPrice = typeof currentPlan.monthlyPrice === 'string' 
      ? parseFloat(currentPlan.monthlyPrice) 
      : currentPlan.monthlyPrice;
    const newPrice = typeof plan.monthlyPrice === 'string' 
      ? parseFloat(plan.monthlyPrice) 
      : plan.monthlyPrice;
    
    const isUpgrade = newPrice > currentPrice;
    const isDowngrade = newPrice < currentPrice;

    // Check limit warnings for downgrade
    const limitWarnings: string[] = [];
    
    // Note: In a real app, you'd check actual usage against new limits
    // For now, we just compare the limits themselves
    if (isDowngrade) {
      if (plan.maxContacts !== -1 && currentPlan.maxContacts === -1) {
        limitWarnings.push(`Contacts limited to ${formatLimitValue(plan.maxContacts)}`);
      } else if (plan.maxContacts !== -1 && plan.maxContacts < currentPlan.maxContacts) {
        limitWarnings.push(`Contacts reduced to ${formatLimitValue(plan.maxContacts)}`);
      }
      
      if (plan.maxMessagesPerMonth !== -1 && currentPlan.maxMessagesPerMonth === -1) {
        limitWarnings.push(`Messages limited to ${formatLimitValue(plan.maxMessagesPerMonth)}/mo`);
      } else if (plan.maxMessagesPerMonth !== -1 && plan.maxMessagesPerMonth < currentPlan.maxMessagesPerMonth) {
        limitWarnings.push(`Messages reduced to ${formatLimitValue(plan.maxMessagesPerMonth)}/mo`);
      }
      
      if (plan.maxTemplates !== -1 && currentPlan.maxTemplates === -1) {
        limitWarnings.push(`Templates limited to ${formatLimitValue(plan.maxTemplates)}`);
      } else if (plan.maxTemplates !== -1 && plan.maxTemplates < currentPlan.maxTemplates) {
        limitWarnings.push(`Templates reduced to ${formatLimitValue(plan.maxTemplates)}`);
      }
      
      if (plan.maxCampaigns !== -1 && currentPlan.maxCampaigns === -1) {
        limitWarnings.push(`Campaigns limited to ${formatLimitValue(plan.maxCampaigns)}`);
      } else if (plan.maxCampaigns !== -1 && plan.maxCampaigns < currentPlan.maxCampaigns) {
        limitWarnings.push(`Campaigns reduced to ${formatLimitValue(plan.maxCampaigns)}`);
      }
    }

    return { isCurrent: false, isUpgrade, isDowngrade, limitWarnings };
  }, [currentSubscription, currentPlan]);

  const quoteKey = JSON.stringify([selectedPlanId, isYearly, couponCode]);
  const matchingQuote = upgradeQuote?.key === quoteKey ? upgradeQuote.data : null;
  useEffect(() => {
    if (!selectedPlanId) return;
    let cancelled = false;
    void subscriptionService.getUpgradeQuote({ planId: selectedPlanId, billingCycle: isYearly ? 'YEARLY' : 'MONTHLY',
      ...(couponCode && { couponCode }) }).then(response => {
      if (cancelled) return;
      if (response.success && response.data) { setUpgradeQuote({ key: quoteKey, data: response.data }); setCouponError(null); }
      else { setUpgradeQuote(null); setCouponError(response.message); }
    });
    return () => { cancelled = true; };
  }, [selectedPlanId, isYearly, couponCode, quoteKey]);

  const handleUpgrade = async () => {
    if (!selectedPlanId || !selectedPaymentMethodId || !matchingQuote) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await subscriptionService.upgradePlan({
        planId: selectedPlanId,
        billingCycle: isYearly ? 'YEARLY' : 'MONTHLY',
        paymentMethodId: selectedPaymentMethodId,
        ...(couponCode && { couponCode }),
      });

      if (response.success) {
        setSuccess(true);
        // Redirect to billing page after 2 seconds
        setTimeout(() => {
          navigate(ROUTES.CUSTOMER.SETTINGS_BILLING);
        }, 2000);
      } else {
        setError(response.message || 'Failed to upgrade plan');
      }
    } catch (err) {
      console.error('Error upgrading plan:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedComparison = selectedPlan ? comparePlan(selectedPlan) : null;

  const isLoading = plansLoading || loadingSubscription || loadingPaymentMethods;

  // Success State
  if (success) {
    return (
      <div className="min-h-screen bg-(--color-dark-navy) flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-2xl p-8 text-center max-w-md"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-emerald-400" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Plan Upgraded!</h2>
          <p className="text-(--color-soft-gray) mb-6">
            Your subscription has been successfully upgraded. You now have access to all the features of your new plan.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-(--color-muted-gray)">
            <RefreshCw size={14} className="animate-spin" />
            <span>Redirecting to billing...</span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--color-dark-navy)">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-(--color-midnight-navy)/95 backdrop-blur-xl border-b border-(--color-border-gray)">
        <div className="container-custom flex items-center justify-between h-16">
          <Link 
            to={ROUTES.CUSTOMER.SETTINGS_BILLING}
            className="flex items-center gap-2 text-(--color-muted-gray) hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Billing</span>
          </Link>
          
          <h1 className="text-lg font-heading font-semibold text-white">Upgrade Plan</h1>
          
          <div className="w-32" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <main className="container-custom py-8">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <RefreshCw size={32} className="text-(--color-signal-orange) animate-spin" />
              <p className="text-(--color-muted-gray)">Loading plans...</p>
            </div>
          </div>
        ) : plansError ? (
          <div className="text-center py-12">
            <p className="text-(--color-error)">{plansError}</p>
          </div>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Page Title */}
            <motion.div variants={fadeInUp} className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-(--color-orange-glow) mb-4">
                <Zap size={28} className="text-(--color-signal-orange)" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-2">
                Upgrade Your Plan
              </h2>
              <p className="text-(--color-soft-gray) max-w-xl mx-auto">
                Choose a new plan to unlock more features and increase your limits.
                {currentPlan && (
                  <span className="block mt-1 text-(--color-muted-gray)">
                    Current plan: <span className="text-emerald-400 font-medium">{currentPlan.name}</span>
                  </span>
                )}
              </p>
            </motion.div>

            {/* Billing Toggle */}
            <motion.div variants={fadeInUp} className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-3 p-1 rounded-xl bg-(--color-navy-light) border border-(--color-border-gray)">
                <button
                  onClick={() => setIsYearly(false)}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                    !isYearly
                      ? 'bg-(--color-signal-orange) text-white'
                      : 'text-(--color-muted-gray) hover:text-white'
                  )}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsYearly(true)}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
                    isYearly
                      ? 'bg-(--color-signal-orange) text-white'
                      : 'text-(--color-muted-gray) hover:text-white'
                  )}
                >
                  Yearly
                  <Badge variant="success" size="sm">Save 17%</Badge>
                </button>
              </div>
            </motion.div>

            {/* Plans Grid */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
            >
              {plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isYearly={isYearly}
                  isSelected={selectedPlanId === plan.id}
                  comparison={comparePlan(plan)}
                  onSelect={() => setSelectedPlanId(plan.id)}
                />
              ))}
            </motion.div>

            {/* Payment & Summary Section */}
            <AnimatePresence>
              {selectedPlan && !comparePlan(selectedPlan).isCurrent && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="grid lg:grid-cols-2 gap-6 mb-8"
                >
                  <PaymentMethodSelector
                    paymentMethods={paymentMethods}
                    selectedMethodId={selectedPaymentMethodId}
                    onSelect={setSelectedPaymentMethodId}
                    onAddNew={() => navigate(ROUTES.CUSTOMER.SETTINGS_PAYMENT_METHODS)}
                  />
                  <div className="space-y-4">
                  <OrderSummary
                    currentPlan={currentPlan ? {
                      name: currentPlan.name,
                      price: typeof currentPlan.monthlyPrice === 'string' 
                        ? parseFloat(currentPlan.monthlyPrice) 
                        : currentPlan.monthlyPrice
                    } : null}
                    newPlan={selectedPlan}
                    isYearly={isYearly}
                    isUpgrade={selectedComparison?.isUpgrade || false}
                    includesLeadGenAddon={!!currentSubscription?.leadGenAddonEnabled}
                    organizationCount={currentSubscription?.organizationCount ?? 1}
                    quote={matchingQuote}
                  />
                  <CouponField
                    value={couponInput}
                    onChange={setCouponInput}
                    onApply={setCouponCode}
                    appliedCode={couponCode}
                    coupon={matchingQuote?.coupon ?? currentSubscription?.coupon}
                    error={couponError}
                    isLoading={!matchingQuote && !couponError}
                    disabled={isSubmitting}
                  />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-6 p-4 rounded-lg bg-(--color-error)/10 border border-(--color-error)/20"
              >
                <p className="text-(--color-error) text-sm">{error}</p>
              </motion.div>
            )}

            {/* Action Button */}
            <AnimatePresence>
              {selectedPlan && !comparePlan(selectedPlan).isCurrent && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="flex justify-center"
                >
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleUpgrade}
                    isLoading={isSubmitting}
                    disabled={!selectedPaymentMethodId || !matchingQuote}
                    rightIcon={<ArrowRight size={18} />}
                    className="min-w-[240px]"
                  >
                    {selectedComparison?.isUpgrade ? 'Upgrade Now' : 'Change Plan'}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Security Note */}
            <motion.div
              variants={fadeInUp}
              className="flex items-center justify-center gap-2 mt-8 text-xs text-(--color-muted-gray)"
            >
              <Shield size={14} />
              <span>Your payment is secured with 256-bit SSL encryption</span>
            </motion.div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export { UpgradePlanPage };

